import React, { useCallback, useEffect, useState } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiFlag, FiX, FiCheck, FiPlay } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { financeAPI } from '../../services/api';
import * as fs from './financeStyles';

const FLAG_LABELS = {
  OUTLIER_AMOUNT: 'Outlier Amount',
  THIN_JUSTIFICATION: 'Thin Intercompany Justification',
  UNRECONCILED_AT_CLOSE: 'Unreconciled at Close',
};

const AnomalyFlags = () => {
  const { isFinanceLeadership } = useAuth();
  const [entities, setEntities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [flags, setFlags] = useState([]);
  const [reviewedFilter, setReviewedFilter] = useState('false');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [scanEntity, setScanEntity] = useState('');
  const [scanPeriod, setScanPeriod] = useState('');
  const [scanning, setScanning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [entRes, perRes, flagRes] = await Promise.all([
        financeAPI.entities.list(), financeAPI.periods.list(),
        financeAPI.anomalyFlags.list(reviewedFilter === '' ? {} : { reviewed: reviewedFilter }),
      ]);
      setEntities(entRes.data.results || entRes.data || []);
      setPeriods(perRes.data.results || perRes.data || []);
      setFlags(flagRes.data.results || flagRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || 'Failed to load anomaly flags.');
    } finally {
      setLoading(false);
    }
  }, [reviewedFilter]);

  useEffect(() => { load(); }, [load]);

  const review = async (flag) => {
    const notes = window.prompt('Review notes (why is this a false positive, or what did you find)?') || '';
    setBusyId(flag.id);
    setError(null);
    try { await financeAPI.anomalyFlags.review(flag.id, notes); load(); }
    catch (e) { setError(e?.response?.data?.error || 'Failed to mark reviewed.'); }
    finally { setBusyId(null); }
  };

  const runScans = async () => {
    if (!scanEntity || !scanPeriod) { setError('Select an entity and period to run scans against.'); return; }
    setScanning(true);
    setError(null);
    try {
      const res = await financeAPI.anomalyFlags.runScans(scanEntity, scanPeriod);
      const created = res.data.length;
      if (created === 0) window.alert('No new anomalies found.');
      load();
    } catch (e) {
      setError(e?.response?.data?.error || 'Failed to run scans.');
    } finally {
      setScanning(false);
    }
  };

  const entityName = (id) => entities.find((e) => e.id === id)?.name || '—';
  const filteredPeriods = periods.filter((p) => !scanEntity || String(p.entity) === String(scanEntity));

  return (
    <div style={fs.page}>
      <div style={fs.pageHeader}>
        <div>
          <h1 style={fs.pageTitle}><FiFlag size={22} />Anomaly Flags</h1>
          <p style={fs.pageSubtitle}>Rule-based "review this" signals — never a block. Reviewing is one-way and never changes anything else.</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={reviewedFilter} onChange={(e) => setReviewedFilter(e.target.value)} style={fs.select}>
            <option value="false">Unreviewed</option>
            <option value="true">Reviewed</option>
            <option value="">All</option>
          </select>
          <button onClick={load} title="Refresh" style={fs.iconButton}><FiRefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} /></button>
        </div>
      </div>

      {error && (
        <div style={fs.errorBanner}>
          <FiAlertTriangle size={18} /><span>{error}</span>
          <button onClick={() => setError(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: fs.colors.red, cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      {isFinanceLeadership() && (
        <div style={{ ...fs.card, marginBottom: 22, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div><div style={fs.label}>Entity</div>
            <select value={scanEntity} onChange={(e) => { setScanEntity(e.target.value); setScanPeriod(''); }} style={fs.select}>
              <option value="">Select…</option>{entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </div>
          <div><div style={fs.label}>Period</div>
            <select value={scanPeriod} onChange={(e) => setScanPeriod(e.target.value)} style={fs.select}>
              <option value="">Select…</option>{filteredPeriods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <button onClick={runScans} disabled={scanning || !scanEntity || !scanPeriod} style={fs.primaryButton}>
            <FiPlay size={13} />{scanning ? 'Scanning…' : 'Run Scans'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={fs.loadingState}>Loading…</div>
      ) : flags.length === 0 ? (
        <div style={fs.emptyState}><FiFlag size={44} style={{ marginBottom: 16 }} /><div style={{ fontSize: 15, color: fs.colors.textGhost }}>Nothing flagged.</div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {flags.map((f) => {
            const busy = busyId === f.id;
            return (
              <div key={f.id} style={{ ...fs.card, border: `1px solid ${f.reviewed ? fs.colors.cardBorder : 'rgba(245,166,35,0.4)'}` }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={fs.badge(fs.colors.amber)}>{FLAG_LABELS[f.flag_type] || f.flag_type}</span>
                      <span style={{ color: fs.colors.textFaint, fontSize: 12 }}>{entityName(f.entity)}</span>
                      {f.journal_entry && <span style={{ color: fs.colors.textFaint, fontSize: 12, fontFamily: 'monospace' }}>JE#{f.journal_entry}</span>}
                    </div>
                    <div style={{ color: fs.colors.text, fontSize: 13, marginTop: 6 }}>{f.description}</div>
                    {f.reviewed && (
                      <div style={{ color: fs.colors.green, fontSize: 12, marginTop: 6 }}>Reviewed: {f.review_notes || '(no notes)'}</div>
                    )}
                  </div>
                  {!f.reviewed && isFinanceLeadership() && (
                    <button onClick={() => review(f)} disabled={busy} style={fs.successButton}><FiCheck size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Mark Reviewed</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AnomalyFlags;
